// src/components/ParlayBuilder.tsx
import { useMemo } from 'react';
import { usePropsStore } from '../stores/propsStore';
import { useParlayStore } from '../stores/parlayStore';
import { Sport, Side } from '../domain/types';
import { PlayerPhoto } from './PlayerPhoto';

const SPORT_COLOR: Record<Sport, string> = { NBA: '#B7FF00', NHL: '#B7FF00' };
const SIDE_LABEL: Record<Side, string> = { over: 'OVER', under: 'UNDER' };
const SIDE_COLOR: Record<Side, string> = { over: '#22C55E', under: '#EF4444' };

export function ParlayBuilder() {
  const { allProps, getAllGames } = usePropsStore();
  const { legs, stake, setStake, addLeg, removeLeg, clearLegs, canBuild, validationError, saveParlay } = useParlayStore();

  // agrupa props por jogo
  const games = useMemo(() => getAllGames(), [allProps]);
  const propsByGame = useMemo(() => {
    const map: Record<string, typeof allProps> = {};
    games.forEach(g => { map[g] = allProps.filter(p => p.game === g); });
    return map;
  }, [allProps, games]);

  const combinedOdds = useMemo(() =>
    legs.reduce((a, b) => a * b.odds, 1), [legs]
  );
  const combinedHitRate = useMemo(() => {
    if (legs.length === 0) return 0;
    const weights = legs.map(p => 1 / p.odds);
    const sumW = weights.reduce((a, b) => a + b, 0);
    return Math.round(legs.reduce((acc, p, i) => acc + (p.hitRatePct ?? 50) * weights[i] / sumW, 0));
  }, [legs]);

  const potentialReturn = stake * combinedOdds;

  return (
    <div style={{ padding: '16px', paddingBottom: 100 }}>
      {/* Seletor de jogo + lista de props */}
      <div style={{ marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 700 }}>Selecionar Pernas</h3>
        {games.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#888', padding: 32 }}>
            Nenhuma prop disponível. Puxe para atualizar.
          </div>
        ) : (
          games.map(game => {
            const props = propsByGame[game];
            const best = props
              .filter(p => typeof p.hitRatePct === 'number')
              .sort((a, b) => (b.hitRatePct ?? 0) - (a.hitRatePct ?? 0))
              .slice(0, 4);
            return (
              <details key={game} style={{ marginBottom: 12, background: 'rgba(255,255,255,0.03)', borderRadius: 12, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.05)' }}>
                <summary style={{ padding: '12px 16px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', fontWeight: 600 }}>
                  <span>{game}</span>
                  <span style={{ color: '#888', fontSize: 12, fontWeight: 400 }}>{props.length} props</span>
                </summary>
                <div style={{ padding: '0 16px 12px', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {best.map(prop => {
                    const isSelected = legs.some(l => l.id === prop.id);
                    return (
                      <button
                        key={prop.id}
                        onClick={() => isSelected ? removeLeg(prop.id) : addLeg(prop)}
                        disabled={isSelected && legs.length >= 10}
                        style={{
                          flex: '1 1 calc(50% - 4px)',
                          minWidth: 140,
                          padding: '10px 12px',
                          background: isSelected ? `${SPORT_COLOR[prop.sport]}22` : 'rgba(255,255,255,0.02)',
                          border: isSelected ? `2px solid ${SPORT_COLOR[prop.sport]}` : '1px solid rgba(255,255,255,0.06)',
                          borderRadius: 10,
                          color: '#fff',
                          textAlign: 'left',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 4,
                          transition: 'all 0.2s',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: 12, fontWeight: 600, color: SPORT_COLOR[prop.sport] }}>{prop.sport}</span>
                          <span style={{ fontSize: 11, background: SIDE_COLOR[prop.side], color: '#000', padding: '2px 6px', borderRadius: 4, fontWeight: 700 }}>
                            {SIDE_LABEL[prop.side]}
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 500 }}><PlayerPhoto pick={prop} className="builder-team-logo"/><span>{prop.player.toUpperCase()}</span></div>
                        <div style={{ fontSize: 11, color: '#888' }}>{prop.market} • Linha {prop.line}</div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                          <span style={{ color: prop.hitRatePct && prop.hitRatePct >= 60 ? '#22C55E' : '#888' }}>
                            HR: {prop.hitRatePct ?? '?'}%
                          </span>
                          <span style={{ fontWeight: 600 }}>@{prop.odds.toFixed(2)}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </details>
            );
          })
        )}
      </div>

      {/* Resumo do Parlay */}
      {legs.length > 0 && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(234,179,8,0.15), rgba(234,179,8,0.05))',
          border: '1px solid rgba(234,179,8,0.3)',
          borderRadius: 16,
          padding: 16,
          marginBottom: 16,
        }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, color: '#B7FF00' }}>
            {legs.length} perna{legs.length > 1 ? 's' : ''} • Odd combinada: {combinedOdds.toFixed(2)}x
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {legs.map((leg, i) => (
              <div key={leg.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 18, height: 18, borderRadius: '50%', background: SPORT_COLOR[leg.sport], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: '#000' }}>
                    {i + 1}
                  </span>
                  <PlayerPhoto pick={leg} className="builder-team-logo" />
                  <span>{leg.player.toUpperCase()} - {SIDE_LABEL[leg.side]} {leg.line}</span>
                </span>
                <button onClick={() => removeLeg(leg.id)} style={{ background: 'none', border: 'none', color: '#888', padding: 4 }}>&times;</button>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid rgba(234,179,8,0.2)', display: 'flex', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 11, color: '#888' }}>HR Combinado</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#B7FF00' }}>{combinedHitRate}%</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 11, color: '#888' }}>Retorno Potencial</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>R$ {potentialReturn.toFixed(2)}</div>
            </div>
          </div>
        </div>
      )}

      {/* Stake + Ações */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <label style={{ fontSize: 12, color: '#888', minWidth: 50 }}>Stake</label>
          <input
            type="number"
            min="1"
            step="1"
            value={stake}
            onChange={e => setStake(Number(e.target.value) || 0)}
            style={{
              flex: 1,
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 10,
              padding: '12px 16px',
              color: '#fff',
              fontSize: 16,
              fontWeight: 600,
            }}
          />
        </div>

        {validationError && (
          <div style={{ padding: '10px 12px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, fontSize: 12, color: '#EF4444' }}>
            {validationError}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => canBuild && saveParlay()}
            disabled={!canBuild}
            style={{
              flex: 1,
              padding: '14px',
              background: canBuild ? 'linear-gradient(135deg, #C9FF36, #B7FF00, #81BB00)' : 'rgba(255,255,255,0.05)',
              border: 'none',
              borderRadius: 12,
              color: canBuild ? '#000' : '#555',
              fontSize: 14,
              fontWeight: 700,
            }}
          >
            {canBuild ? `Salvar Parlay (${legs.length} pernas)` : legs.length < 2 ? 'Mínimo 2 pernas' : 'Máx 10 pernas'}
          </button>
          {legs.length > 0 && (
            <button
              onClick={clearLegs}
              style={{
                padding: '14px 20px',
                background: 'rgba(239,68,68,0.15)',
                border: '1px solid rgba(239,68,68,0.3)',
                borderRadius: 12,
                color: '#EF4444',
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              Limpar
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
